import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AiChoice } from '@cc/shared';
import { GameView } from '../components/GameView';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { useGameStore, type LocalMode } from '../store/gameStore';

const wait = (duration: number) => new Promise((resolve) => window.setTimeout(resolve, duration));

export function GamePage({ mode }: { mode: LocalMode }) {
  const navigate = useNavigate();
  const store = useGameStore();
  const runningAi = useRef(false);

  useEffect(() => {
    if (!store.state || store.mode !== mode) {
      if (!useGameStore.getState().restore(mode)) navigate(`/setup/${mode}`, { replace: true });
    }
  }, [mode, navigate, store.mode, store.state]);

  useEffect(() => {
    if (!store.ceremony) return;
    const timeout = window.setTimeout(() => useGameStore.getState().dismissCeremony(), 3_400);
    return () => window.clearTimeout(timeout);
  }, [store.ceremony]);

  useEffect(() => {
    if (mode !== 'ai' || !store.state || store.ceremony || store.state.phase === 'gameOver' || store.humanIndex === store.state.current || runningAi.current) return;
    runningAi.current = true;
    store.setAiBusy(true);
    const run = async () => {
      if (useGameStore.getState().state?.phase === 'awaitFlip') {
        await wait(800);
        useGameStore.getState().flip();
      }
      const current = useGameStore.getState();
      if (!current.state || current.state.phase !== 'actions') return;
      const worker = new Worker(new URL('../workers/aiWorker.ts', import.meta.url), { type: 'module' });
      const choice = await new Promise<AiChoice>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent<AiChoice>) => resolve(event.data);
        worker.onerror = reject;
        worker.postMessage({ state: current.state, persona: current.config?.difficulty ?? 'penny', seed: crypto.getRandomValues(new Uint32Array(1))[0] ?? 1 });
      }).finally(() => worker.terminate());
      for (const action of choice.actions) {
        await wait(700);
        if (useGameStore.getState().state?.phase !== 'actions') break;
        useGameStore.getState().act(action);
      }
      await wait(500);
      if (useGameStore.getState().state?.phase === 'actions') useGameStore.getState().finishTurn();
    };
    void run().catch((error) => {
      console.error('AI turn failed', error);
      if (useGameStore.getState().state?.phase === 'actions') useGameStore.getState().finishTurn();
    }).finally(() => {
      runningAi.current = false;
      useGameStore.getState().setAiBusy(false);
    });
  }, [mode, store.ceremony, store.humanIndex, store.state?.current, store.state?.phase, store.state?.seq]);

  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      const state = useGameStore.getState().state;
      if (state && state.phase !== 'gameOver') { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, []);

  if (!store.state || store.mode !== mode) return <main className="center-page"><p>Loading your game…</p></main>;
  const state = store.state;
  const interactive = mode === 'local' ? true : store.humanIndex === state.current;
  return <>
    <GameView state={state} setupMode={mode} viewerIndex={mode === 'ai' ? store.humanIndex ?? undefined : undefined} interactive={interactive && !store.handoff && !store.ceremony} busy={store.aiBusy} onFlip={store.flip} onAction={store.act} onUndo={store.undo} onEndTurn={store.finishTurn} onResign={store.resign} onRematch={store.rematch} onLeave={store.abandon} />
    {store.ceremony && <Modal title="Who plays Gold?" destructive><div className="ceremony"><div className="ceremony-coin"><span>?</span></div><h2>{state.players[0].name} plays GOLD and goes first!</h2><Button onClick={store.dismissCeremony}>Let’s play</Button></div></Modal>}
    {store.handoff && <Modal title={`Pass to ${state.players[state.current].name}`} destructive><p>Hand over the device, then tap when ready.</p><Button block onClick={store.acknowledgeHandoff}>I’m ready</Button></Modal>}
  </>;
}
